import React, { useEffect,useState } from 'react';
import { Form, Input,Button, Select, DatePicker, InputNumber, Modal, Space, Row, Col, Upload, message } from 'antd';
import dayjs from 'dayjs';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { CONTRACT_API, DIEUCHINHGIA_API } from '../../constants/api';
import axiosInstance from '../../services/axiosConfig';
const { Option } = Select;
const { TextArea } = Input;

const ContractForm = ({ visible, onCancel, onSubmit, initialValues, title, factories, tenants }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  useEffect(() => {
    if (visible && initialValues) {
      const duration = initialValues.Duration || 0;
      const durationUnit = duration % 12 === 0 ? 'year' : 'month';
      const durationValue = durationUnit === 'year' ? duration / 12 : duration;

      const rawExp = initialValues.ExpirationDate;
      const parsedExp = (typeof rawExp === 'string' && rawExp.includes('-')) 
        ? 0 
        : (rawExp != null ? parseInt(rawExp, 10) || 0 : 0);

      form.setFieldsValue({
        ...initialValues,
        SignDate: initialValues.SignDate && dayjs(initialValues.SignDate).isValid() ? dayjs(initialValues.SignDate) : null,
        StartDate: initialValues.StartDate && dayjs(initialValues.StartDate).isValid() ? dayjs(initialValues.StartDate) : null,
        EndDate: initialValues.EndDate && dayjs(initialValues.EndDate).isValid() ? dayjs(initialValues.EndDate) : null,
        IncreaseRateDate: initialValues.IncreaseRateDate && dayjs.isDayjs(dayjs(initialValues.IncreaseRateDate)) ? dayjs(initialValues.IncreaseRateDate) : null,
        LiquidationDate: initialValues.LiquidationDate && dayjs(initialValues.LiquidationDate).isValid() ? dayjs(initialValues.LiquidationDate) : null,
        durationValue,
        durationUnit,
        YearlyIncreaseRate: initialValues.YearlyIncreaseRate || 0,
        ExpirationDate: parsedExp,
        Status: initialValues.Status || 'Active'
      });
      
      // Khởi tạo fileList từ initialValues nếu có ContractFiles
      if (initialValues.ContractFiles && Array.isArray(initialValues.ContractFiles)) {
        const initialFiles = initialValues.ContractFiles.map((file, index) => ({
          uid: `-${index}`,
          name: `Hợp đồng ${index + 1}`,
          status: 'done',
          url: `${process.env.REACT_APP_API_URL}/${file}`
        }));
        setFileList(initialFiles);
      } else if (initialValues.ContractFile) {
        // Hỗ trợ tương thích ngược với dữ liệu cũ
        setFileList([{
          uid: '-1',
          name: 'Hợp đồng hiện tại',
          status: 'done',
          url: `${process.env.REACT_APP_API_URL}/${initialValues.ContractFile}`
        }]);
      } else {
        setFileList([]);
      }
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      const formData = new FormData();
      
      // Xử lý nhiều file upload
      fileList.forEach((file, index) => {
        if (file.originFileObj) {
          formData.append('contractFiles', file.originFileObj);
        }
      });

      // Xử lý các trường ngày tháng
      if (values.SignDate && dayjs.isDayjs(values.SignDate)) {
        formData.append('SignDate', values.SignDate.format('YYYY-MM-DD'));
      }
      if (values.StartDate && dayjs.isDayjs(values.StartDate)) {
        formData.append('StartDate', values.StartDate.format('YYYY-MM-DD'));
      }
      if (values.EndDate && dayjs.isDayjs(values.EndDate)) {
        formData.append('EndDate', values.EndDate.format('YYYY-MM-DD'));
      }
      if (values.IncreaseRateDate && dayjs.isDayjs(values.IncreaseRateDate)) {
        formData.append('IncreaseRateDate', values.IncreaseRateDate.format('YYYY-MM-DD'));
      }
      if (values.LiquidationDate && dayjs.isDayjs(values.LiquidationDate)) {
        formData.append('LiquidationDate', values.LiquidationDate.format('YYYY-MM-DD'));
      }
      formData.append('ExpirationDate', values.ExpirationDate);

      // Tính toán Duration
      const duration = values.durationUnit === 'year' 
        ? values.durationValue * 12 
        : values.durationValue;
      formData.append('Duration', duration);

      // Append các trường còn lại
      Object.keys(values).forEach(key => {
        if (!['SignDate', 'StartDate', 'EndDate', 'ContractFile', 'ContractFiles', 'durationValue', 'durationUnit', 'IncreaseRateDate', 'LiquidationDate', 'ExpirationDate'].includes(key)) {
          formData.append(key, values[key]);
        }
      });

      // Xử lý Status - nếu có ngày thanh lý thì status sẽ là 'Liquidated'
      const finalStatus = values.LiquidationDate ? 'Liquidated' : (values.Status || 'Active');
      formData.append('Status', finalStatus);

      if (initialValues?.ContractID) {
        await axiosInstance.put(CONTRACT_API.UPDATE(initialValues.ContractID), formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        message.success('Cập nhật hợp đồng thành công');
      } else {
        await axiosInstance.post(CONTRACT_API.CREATE, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        message.success('Tạo hợp đồng thành công');
      }
      
      onCancel();
      form.resetFields();
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error('Có lỗi xảy ra: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const calculateEndDate = (startDate, value, unit) => {
    if (startDate && value) {
      const months = unit === 'year' ? value * 12 : value;
      const endDate = dayjs(startDate).add(months, 'month').subtract(1, 'day');
      form.setFieldsValue({ EndDate: endDate });
    }
  };

  const onDurationChange = () => {
    const startDate = form.getFieldValue('StartDate');
    const value = form.getFieldValue('durationValue');
    const unit = form.getFieldValue('durationUnit');
    calculateEndDate(startDate, value, unit);
  };

  const handlePreview = async (file) => {
    if (file.url) {
      window.open(file.url);
    }
  };

  return (
    <Modal
      visible={visible}
      title={title}
      onCancel={onCancel}
      onOk={handleSubmit}
      width="95%"
      style={{ maxWidth: 1000, top: 20 }}
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', padding: '24px 24px 0' }}
      destroyOnClose
      maskClosable={false}
      centered
      confirmLoading={loading}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...initialValues,
          SignDate: initialValues?.SignDate && dayjs(initialValues.SignDate).isValid() ? dayjs(initialValues.SignDate) : null,
          StartDate: initialValues?.StartDate && dayjs(initialValues.StartDate).isValid() ? dayjs(initialValues.StartDate) : null,
          EndDate: initialValues?.EndDate && dayjs(initialValues.EndDate).isValid() ? dayjs(initialValues.EndDate) : null,
          IncreaseRateDate: initialValues?.IncreaseRateDate && dayjs.isDayjs(dayjs(initialValues.IncreaseRateDate)) ? dayjs(initialValues.IncreaseRateDate) : null,
          LiquidationDate: initialValues?.LiquidationDate && dayjs(initialValues.LiquidationDate).isValid() ? dayjs(initialValues.LiquidationDate) : null,
          durationUnit: 'year',
          YearlyIncreaseRate: initialValues?.YearlyIncreaseRate || 0,
          ExpirationDate: (typeof initialValues?.ExpirationDate === 'string' && initialValues.ExpirationDate.includes('-')) ? 0 : (initialValues?.ExpirationDate != null ? parseInt(initialValues.ExpirationDate, 10) || 0 : 0),
          Status: initialValues?.Status || 'Active'
        }}
        validateTrigger="onBlur"
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="ContractID"
              label="Mã hợp đồng"
              rules={[{ required: true, message: 'Vui lòng nhập mã hợp đồng!' }]}
            >
              <Input placeholder="Nhập mã hợp đồng" />
            </Form.Item>
          </Col>
          
          <Col xs={24} sm={24} md={16}>
            <Form.Item
              name="TenantID"
              label="Khách thuê"
              rules={[{ required: true, message: 'Vui lòng chọn khách thuê!' }]}
            >
              <Select
                placeholder="Chọn khách thuê"
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                style={{ width: '100%' }}
              >
                {tenants?.map(tenant => (
                  <Option key={tenant.TenantID} value={tenant.TenantID}>
                    {tenant.TenantID} - {tenant.TenantName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={24} md={16}>
            <Form.Item
              name="FactoryID"
              label="Nhà xưởng"
              rules={[{ required: true, message: 'Vui lòng chọn nhà xưởng!' }]}
            >
              <Select
                placeholder="Chọn nhà xưởng"
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                style={{ width: '100%' }}
              >
                {factories?.map(factory => (
                  <Option key={factory.FactoryID} value={factory.FactoryID}>
                    {factory.FactoryID} - {factory.FactoryName} ({factory.Area}m²)
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="SignDate"
              label="Ngày ký"
              rules={[{ required: true, message: 'Vui lòng chọn ngày ký!' }]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
                inputReadOnly={true}
                changeOnBlur={false}
                showToday={false}
                onMouseEnter={e => e.preventDefault()}
                onWheel={e => e.preventDefault()}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="StartDate"
              label="Ngày bắt đầu"
              rules={[{ required: true, message: 'Vui lòng chọn ngày bắt đầu!' }]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
                inputReadOnly={true}
                changeOnBlur={false}
                showToday={false}
                onMouseEnter={e => e.preventDefault()}
                onWheel={e => e.preventDefault()}
                onChange={(date) => {
                  form.setFieldsValue({ StartDate: date });
                  onDurationChange();
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item label="Thời hạn" required>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item
                  name="durationValue"
                  rules={[{ required: true, message: 'Vui lòng nhập thời hạn!' }]}
                  noStyle
                >
                  <InputNumber
                    style={{ width: '60%' }}
                    min={1}
                    max={120}
                    placeholder="Nhập thời hạn"
                    onChange={() => onDurationChange()}
                    controls={false}
                  />
                </Form.Item>
                <Form.Item
                  name="durationUnit"
                  rules={[{ required: true, message: 'Vui lòng chọn đơn vị!' }]}
                  noStyle
                >
                  <Select 
                    style={{ width: '40%' }} 
                    onChange={() => onDurationChange()}
                    virtual={false}
                  >
                    <Option value="month">Tháng</Option>
                    <Option value="year">Năm</Option>
                  </Select>
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="EndDate"
              label="Ngày kết thúc"
              rules={[{ required: true, message: 'Vui lòng chọn ngày kết thúc!' }]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
                
                inputReadOnly={true}
                changeOnBlur={false}
                showToday={false}
                onMouseEnter={e => e.preventDefault()}
                onWheel={e => e.preventDefault()}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="RentAmount"
              label="Phí thuê (VNĐ/tháng)"
              rules={[{ required: true, message: 'Vui lòng nhập phí thuê!' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1000000}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="Nhập phí thuê"
                controls={false}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="ServiceFee"
              label="Phí dịch vụ (VNĐ/tháng)"
              rules={[{ required: true, message: 'Vui lòng nhập phí dịch vụ!' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1000000}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="Nhập phí dịch vụ"
                controls={false}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="InfrastructureFee"
              label="Phí hạ tầng (VNĐ/tháng)"
              rules={[{ required: true, message: 'Vui lòng nhập phí hạ tầng!' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1000000}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                placeholder="Nhập phí hạ tầng"
                controls={false}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="PaymentTerm"
              label="Kỳ thanh toán"
            >
              <Select
                placeholder="Chọn kỳ thanh toán"
                style={{ width: '100%' }}
                allowClear
              >
                <Option value={1}>1 tháng</Option>
                <Option value={3}>3 tháng</Option>
                <Option value={6}>6 tháng</Option>
                <Option value={12}>12 tháng</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="YearlyIncreaseRate"
              label="Tỷ lệ tăng giá hàng năm (%)"
              tooltip="Phần trăm tăng giá được áp dụng mỗi năm"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                formatter={value => `${value}%`}
                parser={value => value.replace('%', '')}
                placeholder="Nhập tỷ lệ tăng giá"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="IncreaseRateDate"
              label="Ngày áp dụng tăng giá"
              rules={[{ required: true, message: 'Vui lòng chọn ngày áp dụng tăng giá' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Chọn ngày áp dụng tăng giá"
                inputReadOnly={true}
                changeOnBlur={false}
                showToday={false}
                onMouseEnter={e => e.preventDefault()}
                onWheel={e => e.preventDefault()}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="LiquidationDate"
              label="Ngày thanh lý hợp đồng"
              tooltip="Ngày hợp đồng được thanh lý (nếu có)"
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Chọn ngày thanh lý"
                inputReadOnly={true}
                changeOnBlur={false}
                showToday={false}
                onMouseEnter={e => e.preventDefault()}
                onWheel={e => e.preventDefault()}
                onChange={(date) => {
                  // Tự động cập nhật status khi có ngày thanh lý
                  if (date) {
                    form.setFieldsValue({ Status: 'Liquidated' });
                  } else {
                    form.setFieldsValue({ Status: 'Active' });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="Status"
              label="Trạng thái hợp đồng"
            >
              <Select
                placeholder="Chọn trạng thái"
                style={{ width: '100%' }}
                allowClear
              >
                <Option value="Active">Đang hiệu lực</Option>
                <Option value="Liquidated">Đã thanh lý</Option>
                <Option value="Expired">Đã hết hạn</Option>
                <Option value="Expiring">Sắp hết hạn</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="ContractFiles"
              label="File hợp đồng"
              valuePropName="fileList"
              getValueFromEvent={(e) => {
                if (Array.isArray(e)) {
                  return e;
                }
                setFileList(e?.fileList || []);
                return e?.fileList;
              }}
            >
              <Upload
                multiple
                fileList={fileList}
                beforeUpload={() => false}
                accept=".pdf,.doc,.docx"
                onPreview={handlePreview}
                onRemove={(file) => {
                  const index = fileList.indexOf(file);
                  const newFileList = fileList.slice();
                  newFileList.splice(index, 1);
                  setFileList(newFileList);
                }}
              >
                <Button icon={<UploadOutlined />}>Tải lên file hợp đồng</Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="ExpirationDate"
              label="Số ngày thông báo"
              tooltip="Số ngày thông báo trước khi hết hạn hợp đồng"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                placeholder="Nhập số ngày thông báo"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="Note"
              label="Ghi chú"
            >
              <TextArea rows={4} placeholder="Nhập ghi chú" maxLength={500} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ContractForm;